import { useEffect, useRef, useState } from "react";
import client from "@/lib/api";
import { useRealtime } from "@/lib/realtime";
import { toast } from "sonner";
import { MessageSquare, Send, Loader2 } from "lucide-react";

const CANNED = [
  "Help is on the way. Stay on this page.",
  "Can you get somewhere safe?",
  "Are you hurt? Reply YES or NO.",
  "Is the suspect still there?",
  "What is your exact address or nearest cross street?",
  "Officers are nearby. Stay quiet and keep your phone on silent.",
  "Can you safely unlock a door for responders?",
  "Describe the person — clothing, height, weapon?",
];

// Dispatcher side of Text From 911 — caller messages (web or SMS reply) + dispatcher replies on the call.
export const LocateChat = ({ incident }) => {
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const load = () => client.get(`/incidents/${incident.id}/locate-chat`).then((r) => setMsgs(r.data.messages || [])).catch(() => {});
  useEffect(() => { load(); const iv = setInterval(load, 6000); return () => clearInterval(iv); }, [incident.id]); // eslint-disable-line
  useRealtime(["incidents"], load);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [msgs?.length]);
  const send = async (body = text) => {
    if (!body.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await client.post(`/incidents/${incident.id}/locate-chat`, { text: body });
      setMsgs((m) => [...(m || []), data.message]); setText("");
      toast.success(data.message.sms?.sent ? `Reply texted to ${data.message.sms.to}` : "Reply posted to caller's locate page");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send reply"); } finally { setBusy(false); }
  };
  if (!msgs || (msgs.length === 0 && !incident.caller_location)) return null;
  const unread = msgs.filter((m) => m.dir === "in" && !m.read).length;
  return (
    <div data-testid="locate-chat-panel" className="mt-2 border border-[#3B82F6]/50 bg-[#3B82F6]/10 p-2 space-y-1.5">
      <div className="flex items-center gap-1.5 font-mono text-[10px] font-bold text-[#93C5FD]"><MessageSquare size={11} /> TEXT FROM 911 · caller chat {unread > 0 && <span className="px-1.5 rounded-full bg-[#DC2626] text-white">{unread}</span>}</div>
      <div className="max-h-44 overflow-y-auto space-y-1 pr-1">
        {msgs.length === 0 && <div className="font-mono text-[10px] text-[#9CA3AF] italic">No texts yet — caller can type on their locate page or reply to the SMS.</div>}
        {msgs.map((m, i) => (
          <div key={i} data-testid={`locate-chat-${m.dir}`} className={`max-w-[88%] px-2 py-1 rounded font-mono text-[10px] ${m.dir === "in" ? "bg-[#1E3A8A] text-white" : "ml-auto bg-[#1F2937] text-[#E5E7EB]"}`}>
            <div className="text-[8px] uppercase opacity-70">{m.dir === "in" ? `CALLER · ${m.via}` : `${m.from} · ${m.via}`} · {new Date(m.ts).toLocaleTimeString("en-GB", { hour12: false }).slice(0, 5)}</div>
            {m.text}
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div data-testid="locate-chat-canned" className="flex gap-1 flex-wrap">
        {CANNED.map((c, i) => (
          <button key={i} data-testid={`locate-chat-canned-${i}`} onClick={() => send(c)} disabled={busy} title={c}
            className="px-1.5 py-0.5 text-[9px] font-mono border border-[#3B82F6]/60 text-[#BFDBFE] rounded-sm hover:bg-[#3B82F6]/30 disabled:opacity-50 max-w-full truncate">{c}</button>
        ))}
      </div>
      <div className="flex gap-1.5">
        <input data-testid="locate-chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Reply to caller…"
          className="flex-1 min-w-0 bg-[#141518] border border-[#2C2D35] text-white px-2 py-1 text-[11px] font-mono outline-none" />
        <button data-testid="locate-chat-send" onClick={() => send()} disabled={busy || !text.trim()} className="px-2.5 bg-[#3B82F6] text-white rounded-sm disabled:opacity-50 flex items-center">{busy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}</button>
      </div>
    </div>
  );
};
